import { Allergen } from 'src/apollo/onlineOrdering'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { Selections } from 'public/components/online_ordering/types'
import { t } from 'src/translations'

export type AllergenWithSource = {
  allergen: Allergen
  source: string // Translated source label shown in the allergen info sheet
}

const getSelectedModifierGuids = (
  selections: Selections | undefined
): string[] => {
  if (!selections) return []

  const guids: string[] = []

  const traverse = (sel: Selections) => {
    Object.values(sel).forEach((group) => {
      Object.values(group).forEach((modifier) => {
        if (modifier.quantity > 0) {
          guids.push(modifier.itemGuid)
          if (modifier.modifierGroups) {
            traverse(modifier.modifierGroups)
          }
        }
      })
    })
  }

  traverse(selections)
  return guids
}

const isAllergen = (allergen: Allergen | null): allergen is Allergen =>
  Boolean(allergen)

export const useAllergensWithSource = (
  allergens: (Allergen | null)[] | null | undefined
): AllergenWithSource[] => {
  const { modifiers, modifierAllergenMap } = useModifierContext()

  type AllergenEntry = {
    allergen: Allergen
    baseDish: boolean
    modifierNames: string[]
  }

  const entries = new Map<string, AllergenEntry>()

  for (const allergen of (allergens ?? []).filter(isAllergen)) {
    entries.set(allergen.code, { allergen, baseDish: true, modifierNames: [] })
  }

  // Traverse all levels of the modifier stack so that both root-level and
  // currently-active nested (uncommitted) selections are included.
  // Avoid memoizing this because modifierGroups can be mutated in place,
  // so selected modifiers may change without the object reference changing.
  if (modifiers.length && modifierAllergenMap) {
    const allGuids = modifiers.flatMap((m) =>
      getSelectedModifierGuids(m.modifierGroups)
    )
    for (const guid of allGuids) {
      const modifierData = modifierAllergenMap[guid]
      if (!modifierData) continue
      for (const allergen of (modifierData.allergens ?? []).filter(
        isAllergen
      )) {
        if (!entries.has(allergen.code)) {
          entries.set(allergen.code, {
            allergen,
            baseDish: false,
            modifierNames: []
          })
        }
        const entry = entries.get(allergen.code)!
        if (!entry.modifierNames.includes(modifierData.name)) {
          entry.modifierNames.push(modifierData.name)
        }
      }
    }
  }

  return [...entries.values()].map(({ allergen, baseDish, modifierNames }) => {
    const parts: string[] = []
    if (baseDish) parts.push(t('allergen-info.source-base-dish'))
    if (modifierNames.length > 0) {
      parts.push(
        `${t('allergen-info.source-modifier')} ${modifierNames.join(', ')}`
      )
    }
    return { allergen, source: parts.join(', ') }
  })
}
